// Merging actuals with a forward forecast for the stacked analyzer chart.
//
// Forward values live on SEPARATE keys (`<group>__forecast`) rather than the
// actual keys. That is what lets recharts draw the projected portion in a
// different style while stacking it in the same order and colour, and it keeps
// the boundary at today unambiguous: a row has actual keys or forecast keys,
// never both.

export const FORECAST_SUFFIX = "__forecast";

export const forecastKey = (group) => `${group}${FORECAST_SUFFIX}`;

export const OTHER_LABEL = "Other";

// foldForecastGroups caps the forward segments exactly as the actual stack is
// capped, folding the remainder into "Other".
//
// Without this the forward side was uncapped while the actual side folded at
// STACK_CAP: a SKU breakdown rendered 191 forward groups and the eight-colour
// palette repeated 24 times. Worse than ugly, it was inconsistent — a series
// folded into grey "Other" before today reappeared as its own named, coloured
// segment after it, so the same cost looked like two different things on either
// side of the boundary.
//
// actualGroups is the actual stack's named groups (topGroups, WITHOUT "Other"),
// largest-first. Membership of that list is the single rule: a group the actual
// side named keeps its name forward, and a group the actual side folded folds
// forward too — it cannot be in actualGroups, so it cannot be named here.
//
// Any slots the actual stack did not use (a tenant with fewer than `cap` groups)
// go to the largest forward-only segments, so a service that starts mid-window
// can still be named rather than disappearing into grey.
//
// Returns the groups with folded names rewritten (mergeForecastRows sums the
// collisions) and the forward segment names in the ACTUAL stack's order, so a
// series holds its position and colour across the boundary instead of being
// reordered alphabetically by the SQL's ORDER BY.
export function foldForecastGroups(groups, actualGroups = [], cap = 5, otherLabel = OTHER_LABEL) {
  const list = groups || [];
  if (list.length === 0) return { groups: [], names: [] };

  const kept = new Set(actualGroups || []);
  const forwardTotals = new Map();
  for (const g of list) {
    if (kept.has(g.group)) continue;
    forwardTotals.set(g.group, (forwardTotals.get(g.group) || 0) + (Number(g.cost) || 0));
  }
  const admitted = Array.from(forwardTotals.entries())
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]) || String(a[0]).localeCompare(String(b[0])))
    .slice(0, Math.max(0, cap - kept.size))
    .map(([name]) => name);

  const named = new Set([...kept, ...admitted]);
  let folded = false;
  const out = list.map((g) => {
    if (named.has(g.group)) return g;
    folded = true;
    return { ...g, group: otherLabel };
  });

  const present = new Set(list.map((g) => g.group));
  const names = [...(actualGroups || []).filter((n) => present.has(n)), ...admitted];
  if (folded) names.push(otherLabel);
  return { groups: out, names };
}

// mergeForecastRows appends forward rows after the actual rows and returns the
// day the projection starts, so the chart can draw its boundary. Absent groups
// are zero-filled on forward days, otherwise the stack misaligns and recharts
// drops segments mid-series.
//
// hasActiveFilters is the caller's single source of truth for "is any filter
// (cost-base or perspective/ownership) currently applied" — the same split
// splitFilters already does for every other request this view builds.
// `forecast_total_band_daily` carries no dimensions: it is a whole-project
// total and cannot be filtered. When a filter is active, the segments here
// ARE the filtered, correct picture, but that authoritative total is not —
// it is the unfiltered whole-project figure, several times larger than the
// visible stack. Showing it (or a band built for it) states a number, and a
// precision, that the filtered view does not have. So under any active
// filter the forward __total is recomputed as the sum of the visible forward
// segments (matching what the stack actually shows), and no __band is
// attached — there is no valid uncertainty estimate for a filtered subset,
// and drawing none is the honest answer. Do not "fix" this by borrowing the
// whole-project band back for a filtered view.
export function mergeForecastRows(actualRows, groups, total, groupNames, hasActiveFilters = false) {
  const rows = [...(actualRows || [])];
  if (!groups || groups.length === 0) return { rows, forwardFrom: null };

  const bands = new Map();
  const totals = new Map();
  for (const t of total || []) {
    bands.set(t.day, [t.lower, t.upper]);
    if (t.cost != null) totals.set(t.day, Number(t.cost) || 0);
  }

  const byDay = new Map();
  const order = [];
  for (const g of groups) {
    if (!byDay.has(g.day)) {
      byDay.set(g.day, {});
      order.push(g.day);
    }
    const cells = byDay.get(g.day);
    // ACCUMULATE, never assign: foldForecastGroups rewrites every folded group's
    // name to "Other", so several rows legitimately land on the same cell.
    cells[g.group] = (cells[g.group] || 0) + (Number(g.cost) || 0);
  }
  order.sort();

  for (const day of order) {
    const cells = byDay.get(day);
    const row = { bucket: day };
    let sum = 0;
    for (const name of groupNames) {
      const v = cells[name] || 0;
      row[forecastKey(name)] = v;
      sum += v;
    }
    if (hasActiveFilters) {
      // Filtered: the sum of the visible segments IS the total, and no band —
      // see the function-level comment for why the authoritative table is off
      // limits here.
      row.__total = sum;
    } else {
      const band = bands.get(day);
      if (band) row.__band = band;
      // __total is computed over the actual stack in AnalyzerChart, so the total
      // line and its band used to stop at today — exactly where the band matters
      // most. Carry it across the forward rows.
      //
      // The AUTHORITATIVE total is preferred over the sum of the segments. They are
      // meant to be equal (backend_python reconciles the per-dimension forecast
      // against this very total), and taking the total means a run where they are
      // not shows it: the line lifts off the top of the stack instead of the
      // discrepancy being silently absorbed. It is also the number the band belongs
      // to. Falls back to the segment sum on a day the total table does not cover.
      row.__total = totals.has(day) ? totals.get(day) : sum;
    }
    rows.push(row);
  }

  return { rows, forwardFrom: order[0] };
}
